import React from "react";
import { ExternalLink, TrendingUp, Droplets } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { formatUSD, formatNumber } from "../lib/utils";
import { cn } from "../lib/utils";

function Stat({ label, value, accent }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
        {label}
      </span>
      <span
        className={cn(
          "text-base sm:text-lg font-mono tabular-nums",
          accent === "primary" && "text-[hsl(var(--primary))]"
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function LiquidityPanel({ report }) {
  const dex = report?.dexscreener || {};
  const jp = report?.jupiter || {};
  const pairs = dex.pairs || [];
  const totalLiq = Number(dex.total_liquidity_usd || jp.liquidity || 0);
  const totalVol = Number(dex.total_volume_24h || 0);
  const fdv = Number(jp.fdv || 0);
  const mcap = Number(jp.mcap || 0);
  const price = Number(jp.usdPrice || 0);
  const primary = dex.primary_pair || {};
  return (
    <Card data-testid="liquidity-panel">
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <Droplets size={16} className="text-[hsl(var(--primary))]" />
            Liquidity & Markets
          </span>
        </CardTitle>
        <CardDescription>
          Aggregated across DEXes via DexScreener
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <Stat label="Liquidity" value={formatUSD(totalLiq)} accent="primary" />
          <Stat label="24h Volume" value={formatUSD(totalVol)} />
          <Stat label="Market Cap" value={mcap ? formatUSD(mcap) : "—"} />
          <Stat label="FDV" value={fdv ? formatUSD(fdv) : "—"} />
          <Stat label="Price" value={price ? `$${price.toLocaleString(undefined, { maximumFractionDigits: 8 })}` : "—"} />
          <Stat label="Pairs" value={String(dex.pairs_count ?? pairs.length)} />
          <Stat
            label="Holder Count"
            value={jp.holderCount ? formatNumber(jp.holderCount) : "—"}
          />
          <Stat
            label="Organic Score"
            value={
              jp.organicScore != null
                ? `${Math.round(jp.organicScore)} ${jp.organicScoreLabel ? `(${jp.organicScoreLabel})` : ""}`
                : "—"
            }
          />
        </div>
        {pairs.length ? (
          <div data-testid="liquidity-pairs-table">
            <h4 className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] mb-2">
              Top Pairs
            </h4>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[hsl(var(--muted))]">
                    <th className="px-3 py-2">DEX</th>
                    <th className="px-3 py-2">Pair</th>
                    <th className="px-3 py-2 text-right">Price USD</th>
                    <th className="px-3 py-2 text-right">Liquidity</th>
                    <th className="px-3 py-2 text-right">24h Vol</th>
                    <th className="px-3 py-2 text-right">24h %</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.slice(0, 10).map((p) => {
                    const liqUsd = Number(p?.liquidity?.usd || 0);
                    const v24 = Number(p?.volume?.h24 || 0);
                    const ch24 = Number(p?.priceChange?.h24 || 0);
                    const isPrimary = p?.pairAddress && p.pairAddress === primary?.pairAddress;
                    return (
                      <tr
                        key={p.pairAddress}
                        className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--bg-elev-2)/0.4)]"
                      >
                        <td className="px-3 py-2">
                          <Badge variant="muted" className="capitalize">
                            {p.dexId || "dex"}
                          </Badge>
                          {isPrimary ? (
                            <Badge variant="primary" className="ml-1">Top</Badge>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {p?.baseToken?.symbol || "?"}/{p?.quoteToken?.symbol || "?"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {p?.priceUsd ? `$${Number(p.priceUsd).toLocaleString(undefined, { maximumFractionDigits: 8 })}` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatUSD(liqUsd)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatUSD(v24)}
                        </td>
                        <td
                          className={cn(
                            "px-3 py-2 text-right font-mono tabular-nums",
                            ch24 > 0 && "text-[hsl(var(--success))]",
                            ch24 < 0 && "text-[hsl(var(--danger))]"
                          )}
                        >
                          {Number.isFinite(ch24) ? `${ch24.toFixed(2)}%` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[hsl(var(--muted))] hover:text-[hsl(var(--primary))]"
                            aria-label="Open pair"
                          >
                            <ExternalLink size={13} />
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile */}
            <div className="sm:hidden divide-y divide-[hsl(var(--border))]">
              {pairs.slice(0, 10).map((p) => (
                <a
                  key={p.pairAddress}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between py-2.5"
                >
                  <div>
                    <div className="text-xs font-mono">
                      {p?.baseToken?.symbol}/{p?.quoteToken?.symbol}
                      <span className="ml-2 text-[hsl(var(--muted))]">{p.dexId}</span>
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted))]">
                      {formatUSD(p?.liquidity?.usd || 0)} liq
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-[hsl(var(--muted))]" />
                </a>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-[hsl(var(--muted))]">No DEX pairs found.</p>
        )}
      </CardContent>
    </Card>
  );
}
